/**
 * P2 — Single report source of truth (Pascal-aligned presentation).
 *
 * All report sections (overview, ratios, contributions, balance, EC) must
 * read from `buildReportResultSource(...)`, not invent separate formulas.
 *
 * Result[i] working ppm =
 *   water_background[i] + Σ_salts contributionPpmWorking(stock_g, %, purity, V, CF)
 * Target[i] = Stage 2 formulation setpoints (0 if side-product only).
 */

import { ECCalculationService } from '../services/ECCalculationService';
import {
  getSaltComposition,
  normalizeElementSymbol,
} from './saltElements';
import { normalizePurity } from './UnifiedHydroBuddyEngine';
import { contributionPpmWorking } from './CalculationContext';

export interface ReportSaltInput {
  id: string;
  name: string;
  formula?: string;
  amount: number;
  unit?: string;
  purity?: number;
  elements?: any[];
  tankAssignment?: string;
}

export interface ReportElementRow {
  /** Display / storage symbol (prefer formulation symbol when present) */
  symbol: string;
  /** Normalized key for matching */
  normalized: string;
  /** Stage 2 setpoint ppm (0 for side products) */
  target: number;
  /** Stage 1 water background ppm */
  water: number;
  /** Salt-only contribution ppm (working solution) */
  fromSalts: number;
  /** Final working solution = water + fromSalts (Result[]) */
  achieved: number;
  error: number;
  errorPercent: number;
}

export interface ReportSaltContributionRow {
  saltId: string;
  saltName: string;
  formula: string;
  tankAssignment: string;
  amountG: number;
  symbol: string;
  percentage: number;
  /** Working-solution ppm from this salt × element only */
  contributionPpm: number;
}

export interface ReportResultSource {
  volumeL: number;
  concentrationFactor: number;
  /** One row per element (Result[]) */
  elements: ReportElementRow[];
  /** Per salt × element contribution (sums to fromSalts per symbol) */
  saltContributions: ReportSaltContributionRow[];
  /** EC from achieved ions only (dS/m ≈ mS/cm) */
  electricalConductivity: number;
  ionicStrength: number;
  /** Stage 4 solutionProperties EC if provided and > 0, else recomputed */
  ecSource: 'stage4' | 'recomputed';
  totalDissolvedSolidsG: number;
  warnings: string[];
}

function waterPpm(
  waterElements: Array<{ symbol?: string; concentration?: number }>,
  symbol: string
): number {
  const want = normalizeElementSymbol(symbol);
  let sum = 0;
  for (const w of waterElements || []) {
    const sym = w.symbol || '';
    if (
      normalizeElementSymbol(sym) === want ||
      sym === symbol
    ) {
      sum += Number(w.concentration) || 0;
    }
  }
  return sum;
}

/**
 * Build the single Result[] used by every report section.
 */
export function buildReportResultSource(args: {
  formulationElements?: Array<{ symbol?: string; concentration?: number }>;
  waterElements?: Array<{ symbol?: string; concentration?: number }>;
  saltAmounts?: ReportSaltInput[];
  volumeL?: number;
  concentrationFactor?: number;
  /** Prefer Stage 4 engine achieved when non-empty; still rebuild contributions from salts */
  stageAchieved?: Array<{
    symbol?: string;
    target?: number;
    achieved?: number;
  }>;
  stageEC?: number;
  stageIonicStrength?: number;
}): ReportResultSource {
  const volumeL = Math.max(0.001, Number(args.volumeL) || 1);
  const cf = Math.max(1, Number(args.concentrationFactor) || 1);
  const salts = args.saltAmounts || [];
  const waterElements = args.waterElements || [];
  const formulation = args.formulationElements || [];
  const warnings: string[] = [];

  // --- Per salt × element contributions (working ppm) ---
  const saltContributions: ReportSaltContributionRow[] = [];
  const fromSaltsByNorm = new Map<string, number>();
  const displaySymbolByNorm = new Map<string, string>();

  for (const salt of salts) {
    const composition = getSaltComposition(salt);
    const purity = normalizePurity(salt.purity ?? 1);
    const amountG = Number(salt.amount) || 0;

    if (composition.length === 0 && amountG > 0) {
      warnings.push(`Salt "${salt.name}" has mass but no element % — excluded from Result[].`);
    }

    for (const el of composition) {
      const ppm = contributionPpmWorking(
        amountG,
        el.percentage,
        purity,
        volumeL,
        cf
      );
      // Keep N / N_NO3 / N_NH4 as separate Result channels
      const norm = normalizeElementSymbol(el.symbol) || el.symbol;
      const display =
        norm === 'N'
          ? 'N'
          : norm === 'N_NO3'
            ? 'N_NO3'
            : norm === 'N_NH4'
              ? 'N_NH4'
              : el.symbol;
      if (!displaySymbolByNorm.has(norm)) {
        displaySymbolByNorm.set(norm, display);
      }
      fromSaltsByNorm.set(norm, (fromSaltsByNorm.get(norm) || 0) + ppm);

      saltContributions.push({
        saltId: salt.id,
        saltName: salt.name,
        formula: salt.formula || '',
        tankAssignment: salt.tankAssignment || 'A',
        amountG,
        symbol: display,
        percentage: el.percentage,
        contributionPpm: ppm,
      });
    }
  }

  // Prefer formulation symbols for display when same normalized key
  for (const fe of formulation) {
    const sym = fe.symbol || '';
    if (!sym) continue;
    const norm = normalizeElementSymbol(sym) || sym;
    displaySymbolByNorm.set(norm, sym);
  }

  // Union of formulation + salt + stage achieved symbols
  const allNorms = new Set<string>();
  for (const fe of formulation) {
    if (fe.symbol) allNorms.add(normalizeElementSymbol(fe.symbol) || fe.symbol);
  }
  for (const n of fromSaltsByNorm.keys()) allNorms.add(n);
  for (const ac of args.stageAchieved || []) {
    if (ac.symbol) allNorms.add(normalizeElementSymbol(ac.symbol) || ac.symbol);
  }

  const targetByNorm = new Map<string, number>();
  for (const fe of formulation) {
    const sym = fe.symbol || '';
    if (!sym) continue;
    const norm = normalizeElementSymbol(sym) || sym;
    const t = Number(fe.concentration) || 0;
    targetByNorm.set(norm, (targetByNorm.get(norm) || 0) + t);
  }

  // Stage achieved by normalized symbol (optional overlay if chemistry matches)
  const stageByNorm = new Map<string, { target: number; achieved: number }>();
  for (const ac of args.stageAchieved || []) {
    if (!ac.symbol) continue;
    const norm = normalizeElementSymbol(ac.symbol) || ac.symbol;
    stageByNorm.set(norm, {
      target: Number(ac.target) || 0,
      achieved: Number(ac.achieved) || 0,
    });
  }

  const elements: ReportElementRow[] = [];

  for (const norm of allNorms) {
    if (!norm) continue;
    const symbol = displaySymbolByNorm.get(norm) || norm;
    const target =
      targetByNorm.get(norm) ?? stageByNorm.get(norm)?.target ?? 0;
    const water = waterPpm(waterElements, symbol);
    const fromSalts = fromSaltsByNorm.get(norm) || 0;

    // Prefer chemical rebuild (water + salts). If salts empty but stage has value, use stage.
    let achieved = water + fromSalts;
    const stage = stageByNorm.get(norm);
    if (!(fromSalts > 0) && stage && stage.achieved > 0) {
      achieved = stage.achieved;
    }

    const error = achieved - target;
    const errorPercent = target > 0 ? (error / target) * 100 : 0;

    if (target > 0 || achieved > 0 || water > 0) {
      elements.push({
        symbol,
        normalized: norm,
        target,
        water,
        fromSalts,
        achieved,
        error,
        errorPercent,
      });
    }
  }

  // Stable sort: macros first-ish by target desc then symbol
  elements.sort((a, b) => {
    if (b.target !== a.target) return b.target - a.target;
    return a.symbol.localeCompare(b.symbol);
  });

  // EC from Result achieved ions
  const ecIons = elements
    .filter((e) => e.achieved > 0)
    .map((e) => ({ symbol: e.symbol, concentration: e.achieved }));

  const ecResult = ECCalculationService.calculate(ecIons, 'LMCv2');
  const stageEC = Number(args.stageEC) || 0;
  // Prefer recomputed from Result[] so report is self-consistent; use stage if recompute ~0
  let electricalConductivity = ecResult.predicted;
  let ecSource: 'stage4' | 'recomputed' = 'recomputed';
  if (!(electricalConductivity > 0) && stageEC > 0) {
    electricalConductivity = stageEC;
    ecSource = 'stage4';
  } else if (stageEC > 0 && Math.abs(stageEC - ecResult.predicted) < 0.05) {
    electricalConductivity = stageEC;
    ecSource = 'stage4';
  }

  const totalDissolvedSolidsG = salts.reduce(
    (s, salt) => s + (Number(salt.amount) || 0),
    0
  );

  return {
    volumeL,
    concentrationFactor: cf,
    elements,
    saltContributions,
    electricalConductivity: Math.max(0, electricalConductivity),
    ionicStrength:
      Number(args.stageIonicStrength) > 0
        ? Number(args.stageIonicStrength)
        : ecResult.ionicStrength,
    ecSource,
    totalDissolvedSolidsG,
    warnings,
  };
}

/** Lookup Result ppm by symbol aliases (for ratios). */
export function resultPpm(
  source: ReportResultSource,
  ...aliases: string[]
): number {
  const aliasNorms = new Set(
    aliases.flatMap((a) => [a, normalizeElementSymbol(a)].filter(Boolean))
  );
  let sum = 0;
  let found = false;
  for (const row of source.elements) {
    if (
      aliasNorms.has(row.symbol) ||
      aliasNorms.has(row.normalized) ||
      aliasNorms.has(normalizeElementSymbol(row.symbol))
    ) {
      sum += row.achieved;
      found = true;
    }
  }
  // Avoid double-count: if both N_NO3 and N_NH4 requested separately, caller uses separate calls.
  // When aliases include multiple N forms for total N, sum matching rows once each.
  if (!found) return 0;

  // Deduplicate: if we matched the same row via multiple aliases, we already add once per row.
  return sum;
}

/**
 * Nitrogen channels from Result[]:
 * - no3  = N_NO3 only (never assumed from plain N)
 * - nh4  = N_NH4 only
 * - total = NO3-N + NH4-N when either form exists;
 *           if only a plain "N" row exists (total N, no forms), use that as total
 *           without inventing NO3 or NH4 splits.
 */
export function resultTotalN(source: ReportResultSource): {
  total: number;
  no3: number;
  nh4: number;
} {
  let no3 = 0;
  let nh4 = 0;
  let bulkTotalN = 0;

  for (const row of source.elements) {
    const n = normalizeElementSymbol(row.symbol) || row.normalized;
    if (n === 'N_NO3') {
      no3 += row.achieved;
    } else if (n === 'N_NH4') {
      nh4 += row.achieved;
    } else if (n === 'N') {
      // Explicit total-N channel only — not a synonym for NO3 or NH4
      bulkTotalN += row.achieved;
    }
  }

  if (no3 > 0 || nh4 > 0) {
    // Total N := NH4 + NO3 (do not add bulk N again — would double-count)
    return { total: no3 + nh4, no3, nh4 };
  }
  // Only unlabeled total N available
  return { total: bulkTotalN, no3: 0, nh4: 0 };
}

export function nutrientStatus(
  achieved: number,
  target: number
): { status: string; color: 'success' | 'warning' | 'error' | 'info' } {
  if (!(target > 0)) {
    return { status: 'Info', color: 'info' };
  }
  const ratio = achieved / target;
  if (ratio >= 0.9 && ratio <= 1.1) return { status: 'OK', color: 'success' };
  if (ratio > 1.1) return { status: 'Over', color: 'warning' };
  return { status: 'Low', color: 'error' };
}
